import React, { useMemo, useState } from 'react';
import { useVoteData } from '../../context/VoteDataContext';
import { useSettings } from '../../context/SettingsContext';
import DetailedTeamVotesModal from './DetailedTeamVotesModal';
import BreakdownIcon from '../icons/BreakdownIcon';
import { formatPct } from '../../utils/deadHeat';
import ImpliedOddsInfo from '../common/ImpliedOddsInfo';
// Team logos
import AdlLogo from '../../assets/team_logos/adl.svg';
import BlLogo from '../../assets/team_logos/bl.svg';
import CarlLogo from '../../assets/team_logos/carl.svg';
import CollLogo from '../../assets/team_logos/coll.svg';
import EssLogo from '../../assets/team_logos/ess.svg';
import FreLogo from '../../assets/team_logos/fre.svg';
import GeelLogo from '../../assets/team_logos/geel.svg';
import GcLogo from '../../assets/team_logos/gc.svg';
import GwsLogo from '../../assets/team_logos/gws.svg';
import HawLogo from '../../assets/team_logos/haw.svg';
import MelbLogo from '../../assets/team_logos/melb.svg';
import NmLogo from '../../assets/team_logos/nm.svg';
import PortLogo from '../../assets/team_logos/port.svg';
import RichLogo from '../../assets/team_logos/rich.svg';
import StkLogo from '../../assets/team_logos/stk.svg';
import SydLogo from '../../assets/team_logos/syd.svg';
import WceLogo from '../../assets/team_logos/wce.svg';
import WbdLogo from '../../assets/team_logos/wbd.svg';

const normalize = (value) =>
    (value || '')
        .toString()
        .toLowerCase()
        .replace(/_/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

// Caret shown in a sortable header when that column is the active sort key.
const SortCaret = () => (
    <svg className="inline-block w-2.5 h-2.5 ml-1 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M19 9l-7 7-7-7" />
    </svg>
);

// Marks a per-source cell where the team leads that dataset.
const LeaderCheck = ({ className = 'w-2.5 h-2.5 mt-0.5' }) => (
    <svg className={`${className} text-emerald-400`} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
    </svg>
);

const ChevronDown = ({ className = 'w-3.5 h-3.5' }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 9l-7 7-7-7" />
    </svg>
);

const ROUNDS = Array.from({ length: 25 }, (_, i) => i);

const TEAM_ABBREVIATIONS = {
    "Adelaide_Crows": "ADL",
    "Brisbane_Lions": "BL", 
    "Carlton": "CARL",
    "Collingwood": "COLL",
    "Essendon": "ESS",
    "Fremantle": "FRE",
    "Geelong_Cats": "GEEL",
    "Gold_Coast_Suns": "GC",
    "GWS_Giants": "GWS",
    "Hawthorn": "HAW",
    "Melbourne": "MELB",
    "North_Melbourne": "NM",
    "Port_Adelaide": "PORT",
    "Richmond": "RICH",
    "St_Kilda": "STK",
    "Sydney_Swans": "SYD",
    "West_Coast_Eagles": "WCE",
    "Western_Bulldogs": "WBD"
};

const TEAM_DISPLAY_NAMES = {
    "Adelaide_Crows": "Adelaide",
    "Brisbane_Lions": "Brisbane", 
    "Carlton": "Carlton",
    "Collingwood": "Collingwood",
    "Essendon": "Essendon",
    "Fremantle": "Fremantle",
    "Geelong_Cats": "Geelong",
    "Gold_Coast_Suns": "Gold Coast",
    "GWS_Giants": "GWS",
    "Hawthorn": "Hawthorn",
    "Melbourne": "Melbourne",
    "North_Melbourne": "North Melbourne",
    "Port_Adelaide": "Port Adelaide",
    "Richmond": "Richmond",
    "St_Kilda": "St Kilda",
    "Sydney_Swans": "Sydney",
    "West_Coast_Eagles": "West Coast",
    "Western_Bulldogs": "Western Bulldogs"
};

const getTeamAbbreviation = (teamName) => {
    return TEAM_ABBREVIATIONS[teamName] || teamName;
};

const getTeamDisplayName = (teamName) => {
    return TEAM_DISPLAY_NAMES[teamName] || teamName;
};

const getTeamLogo = (teamName) => {
    const logos = {
        "Adelaide_Crows": AdlLogo,
        "Brisbane_Lions": BlLogo,
        "Carlton": CarlLogo,
        "Collingwood": CollLogo,
        "Essendon": EssLogo,
        "Fremantle": FreLogo,
        "Geelong_Cats": GeelLogo,
        "Gold_Coast_Suns": GcLogo,
        "GWS_Giants": GwsLogo,
        "Hawthorn": HawLogo,
        "Melbourne": MelbLogo,
        "North_Melbourne": NmLogo,
        "Port_Adelaide": PortLogo,
        "Richmond": RichLogo,
        "St_Kilda": StkLogo,
        "Sydney_Swans": SydLogo,
        "West_Coast_Eagles": WceLogo,
        "Western_Bulldogs": WbdLogo
    };
    return logos[teamName] || null;
};

const TeamTotalsTable = () => {
    const { data: voteDataRemote } = useVoteData();
    const { getEnabledKeys } = useSettings();
    const enabledKeys = getEnabledKeys();
    const enabledSet = new Set(enabledKeys);
    const displayOrder = ["count1","count2","count3","count4","count5","betfair","espn","byb"];
    const enabledDisplayKeys = displayOrder.filter((k) => enabledSet.has(k));
    const keyToLabel = { count1: "BG 1", count2: "BG 2", count3: "BG 3", count4: "BG 4", count5: "BG 5", betfair: "BETFAIR", espn: "ESPN", byb: "BYB" };
    const [sortKey, setSortKey] = useState('votes');
    // State for team modal
    const [selectedTeam, setSelectedTeam] = useState(null);
    const [isTeamModalOpen, setIsTeamModalOpen] = useState(false);
    // State for mobile expanded teams
    const [expandedMobileTeams, setExpandedMobileTeams] = useState({});

    const players = voteDataRemote?.leaderboard || [];

    const PLAYER_NAME_TO_TEAM = useMemo(() => {
        const map = new Map();
        for (const p of players) {
            map.set(p.name.toLowerCase(), (p.full_team_name || p.team || "").toString());
        }
        return map;
    }, [players]);

    const buildPerPlayerRoundTotalsFrom = (dataSet) => {
        const result = new Map();
        const data = dataSet && dataSet.game_vote_data ? dataSet.game_vote_data : [];
        for (const roundData of data) {
            const round = String(roundData.round);
            for (const game of roundData.game_votes) {
                for (const vote of game.votes) {
                    const key = vote.player.toLowerCase();
                    if (!PLAYER_NAME_TO_TEAM.has(key)) continue;
                    const expectedTeam = normalize(PLAYER_NAME_TO_TEAM.get(key));
                    if (normalize(vote.team) !== expectedTeam) continue;
                    if (!result.has(key)) result.set(key, {});
                    const r = result.get(key);
                    r[round] = (r[round] || 0) + vote.votes;
                }
            }
        }
        return result;
    };

    const SOURCE_TOTALS = useMemo(() => {
        const wrap = (d) => (Array.isArray(d) ? { game_vote_data: d } : d);
        if (!voteDataRemote) return {
            byb: new Map(), espn: new Map(), betfair: new Map(),
            count1: new Map(), count2: new Map(), count3: new Map(), count4: new Map(), count5: new Map(),
        };
        return {
            byb: buildPerPlayerRoundTotalsFrom(wrap(voteDataRemote.game_vote_data_byb)),
            espn: buildPerPlayerRoundTotalsFrom(wrap(voteDataRemote.game_vote_data_espn)),
            betfair: buildPerPlayerRoundTotalsFrom(wrap(voteDataRemote.game_vote_data_betfair)),
            count1: buildPerPlayerRoundTotalsFrom(wrap(voteDataRemote.game_vote_data_count_1)),
            count2: buildPerPlayerRoundTotalsFrom(wrap(voteDataRemote.game_vote_data_count_2)),
            count3: buildPerPlayerRoundTotalsFrom(wrap(voteDataRemote.game_vote_data_count_3)),
            count4: buildPerPlayerRoundTotalsFrom(wrap(voteDataRemote.game_vote_data_count_4)),
            count5: buildPerPlayerRoundTotalsFrom(wrap(voteDataRemote.game_vote_data_count_5)),
        };
    }, [voteDataRemote, PLAYER_NAME_TO_TEAM]);

    const getSeasonTotal = (sourceMap, playerName) => {
        const key = playerName.toLowerCase();
        const perRound = sourceMap.get(key) || {};
        let sum = 0;
        for (const r of ROUNDS) sum += Number(perRound[String(r)] || 0);
        return sum;
    };

    const teamToPlayers = useMemo(() => {
        const map = new Map();
        for (const p of players) {
            const team = (p.full_team_name || p.team || "").toString();
            if (!map.has(team)) map.set(team, []);
            map.get(team).push(p);
        }
        return map;
    }, [players]);

    const teamTotals = useMemo(() => {
        const result = [];
        for (const [team, playerList] of teamToPlayers.entries()) {
            const teamRow = { team };
            
            // Calculate totals for each source
            for (const k of ["count1","count2","count3","count4","count5","betfair","espn","byb"]) {
                const sourceMap = SOURCE_TOTALS[k];
                let teamTotal = 0;
                for (const player of playerList) {
                    teamTotal += getSeasonTotal(sourceMap, player.name);
                }
                teamRow[k] = teamTotal;
            }
            
            // Calculate average votes over enabled datasets
            const keys = enabledDisplayKeys;
            const n = keys.length || 1;
            const sum = keys.reduce((acc, k) => acc + Number(teamRow[k] || 0), 0);
            teamRow.votes = n > 0 ? sum / n : 0;
            
            result.push(teamRow);
        }
        return result;
    }, [teamToPlayers, SOURCE_TOTALS, enabledDisplayKeys]);

    // Calculate leaders for each source (for winning cell highlighting)
    const sourceLeaders = useMemo(() => {
        const leaders = {};
        for (const k of ["count1","count2","count3","count4","count5","betfair","espn","byb"]) {
            const vals = teamTotals.map((t) => t[k] || 0);
            leaders[k] = vals.length ? Math.max(...vals) : 0;
        }
        return leaders;
    }, [teamTotals]);

    const sortedTeams = useMemo(() => {
        const arr = [...teamTotals];
        arr.sort((a, b) => {
            const va = Number(a[sortKey] || 0);
            const vb = Number(b[sortKey] || 0);
            if (vb !== va) return vb - va; // desc
            return a.team.localeCompare(b.team); // alphabetical fallback
        });
        return arr;
    }, [teamTotals, sortKey]);

    const handleSort = (key) => {
        setSortKey(key);
    };

    // Mobile team rows default to expanded; toggling stores an explicit override.
    const toggleMobileTeam = (teamName) => {
        setExpandedMobileTeams((prev) => ({ ...prev, [teamName]: !(prev[teamName] ?? true) }));
    };

    const isLeaderForSource = (teamValue, sourceMaxValue) => {
        return sourceMaxValue > 0 && teamValue === sourceMaxValue;
    };

    const leaderCellClass = (flag) => (flag ? 'bg-emerald-500/10 ring-1 ring-inset ring-emerald-500/30 rounded-md' : '');

    const renderImpliedCell = (teamRow) => {
        const enabled = enabledDisplayKeys;
        const denom = enabled.length || 1;
        let hits = 0;
        for (const k of enabled) {
            const maxVal = sourceLeaders[k] || 0;
            const val = Number(teamRow[k] || 0);
            if (maxVal > 0 && val === maxVal) hits += 1;
        }
        const hitPct = (hits / denom) * 100;
        if (hitPct === 0) return <span className="text-slate-600 text-[11px] font-mono">N/A</span>;
        const odds = (1 / (hitPct / 100)).toFixed(2);
        return (
            <div className="flex flex-col items-center justify-center gap-0.5">
                <span className="text-emerald-300 font-mono font-bold tabular-nums text-xs md:text-[13px]">{odds}</span>
                <span className="text-slate-500 text-[10px] font-mono tabular-nums">{hits}/{denom} ({formatPct(hitPct)}%)</span>
            </div>
        );
    };

    // Modal handlers
    const openTeamModal = (teamName) => {
        setSelectedTeam(teamName);
        setIsTeamModalOpen(true);
    };

    const closeTeamModal = () => {
        setSelectedTeam(null);
        setIsTeamModalOpen(false);
    };

    if (!players.length) {
        return (
            <div className="bg-[#0a1120] rounded-2xl border border-slate-800 p-4 text-slate-400 text-sm">No data</div>
        );
    }

    return (
        <div>
            {/* Mobile: simplified table (TEAM, VOTES) with expandable rows */}
            <div className="lg:hidden bg-[#0a1120] rounded-2xl border border-slate-800 overflow-hidden">
                <div className="overflow-hidden">
                    <table className="w-full table-fixed">
                        <colgroup>
                            <col className="w-[70%]" />
                            <col className="w-[30%]" />
                        </colgroup>
                        <thead>
                            <tr className="bg-slate-800/20 border-b border-slate-800/80">
                                <th className="w-[70%] text-center pl-2 pr-2 py-2.5 text-[10px] font-mono font-medium text-slate-500 uppercase tracking-[0.12em]">Team</th>
                                <th className="w-[30%] text-center pl-2 pr-4 py-2.5 text-[10px] font-mono font-medium text-emerald-300 uppercase tracking-[0.12em] bg-emerald-500/10">Votes</th>
                            </tr>
                        </thead>
                        <tbody>
                            {sortedTeams.map((teamRow) => {
                                const isExpanded = expandedMobileTeams[teamRow.team] ?? true;
                                
                                return (
                                    <React.Fragment key={`mobile-${teamRow.team}`}>
                                        <tr className="border-b border-slate-800/50 hover:bg-slate-800/40 transition-colors">
                                            <td className="w-[70%] pl-2 pr-2 py-2 align-top">
                                                <div 
                                                    className="flex items-center gap-3 cursor-pointer hover:text-emerald-300 transition-colors"
                                                    onClick={() => openTeamModal(teamRow.team)}
                                                    title="View team vote breakdown"
                                                >
                                                    {getTeamLogo(teamRow.team) ? (
                                                        <div className="w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center flex-shrink-0 ring-1 ring-slate-700/60">
                                                            <img
                                                                src={getTeamLogo(teamRow.team)}
                                                                alt={`${teamRow.team} logo`}
                                                                className="w-5 h-5 object-contain"
                                                            />
                                                        </div>
                                                    ) : (
                                                        <div className="w-8 h-8 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full flex items-center justify-center flex-shrink-0 ring-1 ring-slate-700/60">
                                                            <span className="text-white font-bold text-xs">{getTeamAbbreviation(teamRow.team)}</span>
                                                        </div>
                                                    )}
                                                    <div className="min-w-0 flex-1">
                                                        <div className="flex items-center gap-1.5 min-w-0 text-white font-semibold text-xs">
                                                            <span className="truncate">{getTeamDisplayName(teamRow.team)}</span>
                                                            <BreakdownIcon className="w-3 h-3 flex-shrink-0 text-emerald-400/60" />
                                                        </div>
                                                        <div className="text-[10px] font-mono uppercase tracking-[0.1em] text-slate-500">Team Totals</div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="w-[30%] pl-2 pr-4 py-2 align-top text-right bg-emerald-500/[0.06]">
                                                <div className="inline-flex items-center justify-end gap-2 pr-1">
                                                    <span className="text-emerald-300 font-mono font-bold tabular-nums text-sm">{teamRow.votes.toFixed(2)}</span>
                                                    <button
                                                        className={`w-7 h-7 flex items-center justify-center rounded-md border cursor-pointer transition-all duration-200 ${isExpanded ? 'rotate-180 bg-emerald-500/10 border-emerald-500/30 text-emerald-300' : 'border-slate-700/60 text-slate-400 hover:text-slate-200 hover:border-slate-600'}`}
                                                        aria-label={isExpanded ? 'Collapse team' : 'Expand team'}
                                                        onClick={() => toggleMobileTeam(teamRow.team)}
                                                    >
                                                        <ChevronDown />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                        {isExpanded && (
                                            <tr className="bg-[#060b16] border-b border-slate-800/50">
                                                <td colSpan={2} className="px-0 pb-0">
                                                    {/* Mobile Team Totals Table */}
                                                    <div className="overflow-x-auto">
                                                        <table className="min-w-[720px] w-full">
                                                            <thead>
                                                                <tr className="bg-slate-800/20 border-b border-slate-800/80">
                                                                    {enabledDisplayKeys.map((k) => (
                                                                        <th 
                                                                            key={`mh-${teamRow.team}-${k}`} 
                                                                            onClick={() => handleSort(k)}
                                                                            className="text-center px-1 py-2 text-[10px] font-mono font-medium text-slate-500 uppercase tracking-[0.12em] cursor-pointer select-none hover:text-slate-300 transition-colors"
                                                                        >
                                                                            {keyToLabel[k]} {sortKey === k && <SortCaret />}
                                                                        </th>
                                                                    ))}

                                                                    <th className="text-center px-2 py-2 text-[10px] font-mono font-medium text-slate-500 uppercase tracking-[0.12em]"><span className="inline-flex items-center justify-center">Implied Odds<ImpliedOddsInfo /></span></th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                <tr>
                                                                    {enabledDisplayKeys.map((k) => (
                                                                        <td key={`mv-${teamRow.team}-${k}`} className={`px-1 py-2 text-center ${leaderCellClass(isLeaderForSource(teamRow[k] || 0, sourceLeaders[k]))}`}>
                                                                            <div className="flex flex-col items-center">
                                                                                <span className="text-slate-400 font-mono tabular-nums text-xs">{(teamRow[k] || 0).toFixed(2)}</span>
                                                                                {isLeaderForSource(teamRow[k] || 0, sourceLeaders[k]) && (
                                                                                    <LeaderCheck />
                                                                                )}
                                                                            </div>
                                                                        </td>
                                                                    ))}

                                                                    <td className="px-2 py-2 text-center">
                                                                        {renderImpliedCell(teamRow)}
                                                                    </td>
                                                                </tr>
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Desktop: Team Totals Table */}
            <div className="hidden lg:block bg-[#0a1120] rounded-2xl border border-slate-800 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full md:min-w-[900px]">
                        <thead>
                            <tr className="bg-slate-800/20 border-b border-slate-800/80">
                                <th className="text-left px-2 md:px-4 py-2.5 text-[10px] font-mono font-medium text-slate-500 uppercase tracking-[0.12em] min-w-[150px]">Team</th>
                                {enabledDisplayKeys.map((k) => (
                                    <th 
                                        key={`h-${k}`} 
                                        onClick={() => handleSort(k)}
                                        className="text-center px-1 md:px-2 py-2.5 text-[10px] font-mono font-medium text-slate-500 uppercase tracking-[0.12em] cursor-pointer select-none hover:text-slate-300 transition-colors"
                                    >
                                        {keyToLabel[k]} {sortKey === k && <SortCaret />}
                                    </th>
                                ))}
                                <th 
                                    onClick={() => handleSort('votes')}
                                    className="text-center px-2 md:px-4 py-2.5 text-[10px] font-mono font-medium text-emerald-300 uppercase tracking-[0.12em] cursor-pointer select-none bg-emerald-500/10 hover:text-emerald-200 transition-colors"
                                >
                                    Votes {sortKey === 'votes' && <SortCaret />}
                                </th>
                                <th className="text-center px-2 md:px-4 py-2.5 text-[10px] font-mono font-medium text-slate-500 uppercase tracking-[0.12em]"><span className="inline-flex items-center justify-center">Implied Odds<ImpliedOddsInfo /></span></th>
                            </tr>
                        </thead>
                        <tbody>
                            {sortedTeams.map((teamRow) => (
                                <tr key={teamRow.team} className="border-b border-slate-800/50 hover:bg-slate-800/40 transition-colors">
                                    <td className="px-2 md:px-4 py-2 min-w-[150px]">
                                        <div 
                                            className="flex items-center gap-3 cursor-pointer group/team"
                                            onClick={() => openTeamModal(teamRow.team)}
                                            title="View team vote breakdown"
                                        >
                                            {getTeamLogo(teamRow.team) ? (
                                                <div className="w-8 h-8 md:w-9 md:h-9 bg-emerald-500 rounded-full flex items-center justify-center flex-shrink-0 ring-1 ring-slate-700/60">
                                                    <img
                                                        src={getTeamLogo(teamRow.team)}
                                                        alt={`${teamRow.team} logo`}
                                                        className="w-5 h-5 md:w-6 md:h-6 object-contain"
                                                    />
                                                </div>
                                            ) : (
                                                <div className="w-8 h-8 md:w-9 md:h-9 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full flex items-center justify-center flex-shrink-0 ring-1 ring-slate-700/60">
                                                    <span className="text-white font-bold text-xs">{getTeamAbbreviation(teamRow.team)}</span>
                                                </div>
                                            )}
                                            <div className="min-w-0 flex-1">
                                                <div className="flex items-center gap-1.5 min-w-0 text-white font-semibold text-xs md:text-[13px] group-hover/team:text-emerald-300 transition-colors">
                                                    <span className="truncate">{getTeamDisplayName(teamRow.team)}</span>
                                                    <BreakdownIcon className="w-3 h-3 flex-shrink-0 text-emerald-400/60" />
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                    {enabledDisplayKeys.map((k) => (
                                        <td key={`v-${teamRow.team}-${k}`} className={`px-1 md:px-2 py-2 text-center ${leaderCellClass(isLeaderForSource(teamRow[k] || 0, sourceLeaders[k]))}`}>
                                            <div className="flex flex-col items-center">
                                                <span className="text-slate-400 font-mono tabular-nums text-xs md:text-[13px]">{(teamRow[k] || 0).toFixed(2)}</span>
                                                {isLeaderForSource(teamRow[k] || 0, sourceLeaders[k]) && (
                                                    <LeaderCheck />
                                                )}
                                            </div>
                                        </td>
                                    ))}
                                    <td className="px-2 md:px-4 py-2 text-center bg-emerald-500/[0.06]">
                                        <span className="text-emerald-300 font-mono font-bold tabular-nums text-sm md:text-[15px]">{teamRow.votes.toFixed(2)}</span>
                                    </td>
                                    <td className="px-2 md:px-4 py-2 text-center">
                                        {renderImpliedCell(teamRow)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Team Details Modal */}
            <DetailedTeamVotesModal
                isOpen={isTeamModalOpen}
                onClose={closeTeamModal}
                team={selectedTeam}
                teamData={selectedTeam ? teamToPlayers.get(selectedTeam) : null}
                teamTotals={selectedTeam ? sortedTeams.find(t => t.team === selectedTeam) : null}
            />
        </div>
    );
};

export default TeamTotalsTable; 